import { useEffect, useState } from 'react';
import { useRecoilState, useSetRecoilState, useResetRecoilState, useRecoilValue } from 'recoil';
import TermsAndConditionsModal from './TermsAndConditionsModal';
import useApiCaller from '../hooks/use-api-caller';
import { IPrivacyConsentRequest } from '../types/admin';
import {
  isPrivacyNoticeOpenAtom,
  isPrivacyNoticeCheckedAtom,
  isTrialNoticeOpenAtom,
  isTrialNoticeCheckedAtom,
} from '../store/privacy-consent';
import Loading from './Loading';
import { getUserInfo } from '../utils/user-info';
import { userInfoAtom } from '../store/user-info';
import TrialConsent from './TrialConsent';
import PrivacyConsent from './PrivacyConsent';
import { useUnifiedAuth } from '../auth/AuthContext';
import { authErrorAtom } from '../store/auth';

export default function FtuConsent() {
  const [showPrivacyNotice, setShowPrivacyNotice] = useRecoilState(isPrivacyNoticeOpenAtom);
  const [showTrialNotice, setShowTrialNotice] = useRecoilState(isTrialNoticeOpenAtom);
  const [isPrivacyChecked] = useRecoilState(isPrivacyNoticeCheckedAtom);
  const [isTrialChecked] = useRecoilState(isTrialNoticeCheckedAtom);
  const [loading, setLoading] = useState(true);
  const apiCaller = useApiCaller();
  const { unifiedAuthState, logout } = useUnifiedAuth();
  const isAuthenticated = unifiedAuthState?.isAuthenticated;
  const setUserInfo = useSetRecoilState(userInfoAtom);
  const resetUserInfo = useResetRecoilState(userInfoAtom);
  const setAuthError = useSetRecoilState(authErrorAtom);
  const authError = useRecoilValue(authErrorAtom);

  const initConsentStatus = async () => {
    try {
      const localUserInfo = await getUserInfo();
      const validUserResponse = await apiCaller.validateUserWithInfo(localUserInfo);
      setUserInfo({
        ...localUserInfo,
        subscriptions: validUserResponse.subscriptions,
      });
      if (unifiedAuthState?.authProvider === 'dj') {
        setShowPrivacyNotice(false);
        setShowTrialNotice(false);
      } else {
        setShowPrivacyNotice(!validUserResponse.accepted_privacy_consent);
        setShowTrialNotice(!validUserResponse.trial_ok);
      }

      // log out if the token is not allowed
      //   this occurs when the user has logged out and added the token to the black list
      //   and then uses some back door attack like using cURL to access the same token
      if (!validUserResponse.valid_token) {
        console.error('Invalid token detected. Logging out...');
        handleLogout();
      }
    } catch (err) {
      console.error('Error fetching user info:', err);
      setAuthError('An unexpected error occurred. Please try again later.');
      resetUserInfo();
      logout();
    } finally {
      setLoading(false);
    }
  };

  const handleConsentAccept = () => {
    const newConsent: IPrivacyConsentRequest = {
      accepted_privacy_consent: showPrivacyNotice ? isPrivacyChecked : null,
      accepted_trial_consent: showTrialNotice && !showPrivacyNotice ? isTrialChecked : null,
    };

    apiCaller
      .savePrivacyConsent(newConsent)
      .then(() => {
        if (showTrialNotice && !showPrivacyNotice) {
          // completed second step
          setShowPrivacyNotice(false);
          setShowTrialNotice(false);
        } else {
          //completed first step
          setShowPrivacyNotice(false);
        }
      })
      .catch((err) => console.error(err));
  };

  const handleLogout = () => {
    resetUserInfo();
    logout();
    localStorage.clear();
  };

  useEffect(() => {
    if (unifiedAuthState?.isAuthenticated) {
      initConsentStatus();
    }
  }, [unifiedAuthState]);

  return (
    <div>
      {(showTrialNotice || showPrivacyNotice) && isAuthenticated && (
        <div className={`fixed top-0 bottom-0 left-0 right-0 z-[100] px-8 py-10 ${loading || authError ? 'bg-white' : 'bg-black'}`}>
          {loading && (
            <div className="h-full flex justify-center items-center">
              <Loading />
            </div>
          )}
          {!loading && !authError && (
            <div className="flex flex-col justify-between overflow-y-auto h-full md:h-auto md:w-1/2 md:mx-auto">
              {showTrialNotice && !showPrivacyNotice && <TrialConsent />}
              {showPrivacyNotice && <PrivacyConsent />}
              <button
                className="w-full bg-white text-black font-mmc p-2 disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={(showPrivacyNotice && !isPrivacyChecked) || (showTrialNotice && !showPrivacyNotice && !isTrialChecked)}
                onClick={handleConsentAccept}
              >
                Continue
              </button>

              <button className="w-full bg-white text-black font-mmc p-2 mt-4" onClick={handleLogout}>
                Decline & Logout
              </button>
            </div>
          )}
          <TermsAndConditionsModal />
        </div>
      )}
    </div>
  );
}
